import React, { useState } from 'react';
import {
  Fab,
  Tooltip,
  Zoom,
  Box,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useTranslation } from 'react-i18next';

/**
 * Компонент с кнопками управления туториалом
 *
 * @param {Object} props - свойства компонента
 * @param {boolean} props.showHelpButton - показывать ли кнопку помощи
 * @param {boolean} props.showScrollTopButton - показывать ли кнопку прокрутки наверх
 * @param {boolean} props.isOnboardingActive - активен ли туториал
 * @param {boolean} props.isGuideCompleted - завершен ли гайд
 * @param {Function} props.onHelp - обработчик нажатия кнопки помощи
 * @param {Function} props.onRestart - обработчик нажатия кнопки перезапуска
 * @param {Function} props.onScrollTop - обработчик нажатия кнопки прокрутки наверх
 * @param {Array} props.availableGuides - список доступных гайдов
 * @param {Function} props.onSelectGuide - обработчик выбора гайда из списка
 */
const TutorialControls = ({
  showHelpButton,
  showScrollTopButton,
  isOnboardingActive,
  isGuideCompleted,
  onHelp,
  onRestart,
  onScrollTop,
  availableGuides = [],
  onSelectGuide,
}) => {
  const { t, i18n } = useTranslation(['onboarding', 'common']);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // --- Локализация имен гайдов ---
  const lang = i18n.language || 'en';
  
  // Функция для получения локализованного значения из строки или объекта
  const getLocalized = (val) => {
    if (!val) return '';
    if (typeof val === 'string') return val;
    return val[lang] || val['en'] || val['ru'] || '';
  };

  const handleDefaultAction = () => {
    if (isOnboardingActive) {
      onRestart && onRestart();
    } else if (isGuideCompleted) {
      onRestart && onRestart();
    } else {
      onHelp && onHelp();
    }
  };

  const handleHelpClick = (event) => {
    if (availableGuides && availableGuides.length > 0) {
      setAnchorEl(!anchorEl ? event.currentTarget : null);
    } else {
      handleDefaultAction();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleGuideSelect = (guideId) => {
    handleClose();
    if (onSelectGuide) {
      onSelectGuide(guideId);
    }
  };

  // Кнопка справки/помощи
  const renderHelpButton = () => {
    if (!showHelpButton) return null;

    return (
      <Box
        sx={{
          position: 'fixed',
          bottom: showScrollTopButton ? 80 : 20,
          right: 20,
          zIndex: isOnboardingActive ? 1 : 9000,
        }}
      >
        <Tooltip
          title={t('onboarding:help.button')}
          placement="left"
          disableFocusListener
          disableTouchListener
        >
          <Badge
            color="secondary"
            variant="dot"
            overlap="circular"
            invisible={isOnboardingActive}
            sx={{
              '& .MuiBadge-badge': {
                right: 10,
                top: 10,
              },
            }}
          >
            <Fab
              color="primary"
              size="medium"
              className="help-button"
              sx={{
                bgcolor: isOnboardingActive ? 'primary.light' : 'primary.main',
                transition: 'all 0.3s ease-in-out',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  bgcolor: isOnboardingActive
                    ? 'primary.main'
                    : 'primary.light',
                  transform: 'scale(1.05)',
                },
                animation: isOnboardingActive ? 'none' : 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': {
                    boxShadow: '0 0 0 0 rgba(74, 144, 226, 0.4)',
                  },
                  '70%': {
                    boxShadow: '0 0 0 10px rgba(74, 144, 226, 0)',
                  },
                  '100%': {
                    boxShadow: '0 0 0 0 rgba(74, 144, 226, 0)',
                  },
                },
              }}
              onClick={handleHelpClick}
              aria-controls={open ? 'guide-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
            >
              <QuestionMarkIcon />
            </Fab>
          </Badge>
        </Tooltip>

        {/* Меню со списком гайдов */}
        <Menu
          id="guide-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'help-button',
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          PaperProps={{
            elevation: 3,
            sx: {
              minWidth: 220,
              maxWidth: 320,
              borderRadius: '16px',
            },
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ px: 2, py: 1, fontWeight: 600, color: 'text.secondary' }}
          >
            {t('onboarding:availableGuides')}
          </Typography>

          {availableGuides && availableGuides.length > 0 ? (
            availableGuides.map((guide) => (
              <MenuItem
                key={guide._id}
                onClick={() => handleGuideSelect(guide._id)}
                sx={{
                  py: 1,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon>
                  <PlayArrowIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={getLocalized(guide.name)}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: 500,
                  }}
                />
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>
              <ListItemText
                primary={t('onboarding:noGuides')}
              />
            </MenuItem>
          )}
        </Menu>
      </Box>
    );
  };

  // Кнопка прокрутки наверх
  const renderScrollTopButton = () => {
    return (
      <Zoom in={showScrollTopButton}>
        <Fab
          size="small"
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: isOnboardingActive ? 1 : 9000,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              bgcolor: 'primary.light',
              transform: 'translateY(-3px)',
            },
            transition: 'transform 0.2s ease',
          }}
          onClick={onScrollTop}
          aria-label={t('onboarding:ui.scrollTop')}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Zoom>
    );
  };

  return (
    <>
      {renderHelpButton()}
      {showScrollTopButton && renderScrollTopButton()}
    </>
  );
};

export default TutorialControls;
